<template>
  <v-app>
    <!-- Loading screen while determining auth state -->
    <div v-if="authLoading" class="auth-loading">
      <v-container fill-height class="d-flex align-center justify-center">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            class="mb-4"
          ></v-progress-circular>
          <h3>Loading...</h3>
          <p class="text--secondary">Checking authentication status</p>
        </div>
      </v-container>
    </div>
    
    <!-- Main app content -->
    <div v-else>
      <Header :user="user" :key="user && user.role ? user.role : 'Default'" @logout="logout()"></Header>
      <router-view :user="user" />
    </div>
  </v-app>
</template>
<script>
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { getFirestore, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { app } from './firebase/init'
import Header from './components/Header.vue'
export default {
  name: 'App',
  components: {
    Header
  },
  data: () => ({
    user: null,
    authLoading: true
  }),
  async created() {
    // Use the already initialized Firebase app
    const auth = getAuth(app)
    const db = getFirestore(app)
    
    // Listen for authentication state changes
    onAuthStateChanged(auth, async (user) => {
      console.log('🔑 Auth state changed:', user?.email || 'No user')
      
      this.authLoading = true
      
      if (user) {
        try {
          console.log('📧 Checking for user document by email:', user.email)
          
          // Check by email first (email-based document)
          const emailUserRef = doc(db, 'users', user.email)
          const emailUserSnap = await getDoc(emailUserRef)
          
          if (emailUserSnap.exists()) {
            console.log('✅ Found existing user document by email')
            const userData = emailUserSnap.data()
            console.log('👤 User data:', {
              email: userData.email,
              role: userData.role,
              roles: userData.roles,
              divisions: userData.divisions,
              groups: userData.groups,
              tool_permissions: Object.keys(userData.tool_permissions || {}),
              invited_by: userData.invited_by
            })
            
            // Update last login if document exists and user is invited
            if (userData && userData.role !== undefined) {
              // Try to update last_login, but don't fail if permissions are insufficient
              try {
                await updateDoc(emailUserRef, { last_login: new Date() })
                console.log('📅 Updated last_login timestamp')
              } catch (error) {
                console.warn('⚠️ Could not update last_login (this is non-critical):', error)
              }
              
              this.user = user
              this.user.role = userData.role || (userData.roles && userData.roles.includes('Admin') ? 'Admin' : 
                                                 userData.roles && userData.roles.includes('Manager') ? 'Manager' :
                                                 userData.roles && userData.roles.includes('Pioneer') ? 'Pioneer' : 'Employee')
              this.user.roles = userData.roles // Keep for backward compatibility
              this.user.divisions = userData.divisions || []
              this.user.groups = userData.groups || []
              this.user.tool_permissions = userData.tool_permissions || {}
              console.log('✅ User setup complete:', {
                email: this.user.email,
                role: this.user.role,
                divisions: this.user.divisions,
                groups: this.user.groups,
                tool_permissions: Object.keys(this.user.tool_permissions || {})
              })
            }
          } else {
            // Truly brand new user (no email-based or UID-based document exists)
            console.log('🆕 New User - Attempting to create user document')
            const defaultRole = 'Employee'
            try {
              console.log('💾 Creating new user document with default settings...')
              await setDoc(emailUserRef, {
                email: user.email,
                uid: user.uid,
                role: defaultRole,
                tool_permissions: {},
                divisions: [], // No default assignments - requires admin setup
                groups: [], // No default assignments - requires admin setup
                manager_status: {},
                profile: {
                  name: user.displayName,
                  photo: user.photoURL,
                  emailVerified: user.emailVerified,
                },
                invited_by: 'system',
                invited_at: new Date(),
                created: new Date(),
                last_login: new Date()
              })
              
              console.log('✅ New user document created successfully!')
              
              // Document created successfully
              this.user = user
              this.user.role = defaultRole
              this.user.divisions = []
              this.user.groups = []
              this.user.tool_permissions = {}
              console.log('✅ New user setup complete:', {
                email: this.user.email,
                role: this.user.role,
                divisions: [],
                groups: [],
                tool_permissions: {}
              })
              
            } catch (error) {
              console.error('❌ Could not create new user document:', error)
              
              // Check if this is a permission error (user not invited)
              if (error.code === 'permission-denied' || error.message.includes('permission') || error.message.includes('Missing or insufficient permissions')) {
                console.log('🚫 User not invited - redirecting to uninvited user page')
                this.authLoading = false
                this.$router.push({ name: 'UninvitedUser' })
                return
              } else {
                // Some other error, try to continue but they might be limited
                console.warn('⚠️ Unknown error creating user, continuing with limited access')
                this.user = user
                this.user.role = defaultRole
                this.user.divisions = []
                this.user.groups = []
                this.user.tool_permissions = {}
              }
            }
          }
          
          // Navigate to dashboard if on landing/login page (only if user is properly set up)
          if (this.user && (this.$route.name === 'Landing' || this.$route.name === 'Login')) {
            // Use a small delay to ensure router guards have completed
            this.$nextTick(() => {
              try {
                if (this.$route.name === 'Landing' || this.$route.name === 'Login') {
                  this.go('Dashboard')
                }
              } catch (err) {
                // Ignore navigation errors
                if (err.name !== 'NavigationDuplicated') {
                  console.error('Navigation error:', err)
                }
              }
            })
          }
          
          console.log('auth state changed', user)
          
        } catch (error) {
          console.error('Error in authentication flow:', error)
          this.authLoading = false // Stop loading before logout
          this.logout()
          return
        }
      } else {
        this.authLoading = false // Stop loading before logout
        this.logout()
        return
      }
    
      // Auth state determination complete
      this.authLoading = false
    })
  },
  methods: {
    logout() {
      const auth = getAuth()
      signOut(auth).then(() => { // Sign-out successful.
        this.user = null
        this.authLoading = false // Ensure loading is stopped
        try {
          if (this.$route.name !== 'Landing') {
            this.go('Landing')
          }
        } catch (err) {
          // Ignore navigation errors
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        }
      }).catch((error) => { // An error happened.
        console.log('Something went wrong. Could not sign out.')
        this.authLoading = false // Ensure loading is stopped
        try {
          if (this.$route.name !== 'Unknown') {
            this.go('Unknown')
          }
        } catch (err) {
          // Ignore navigation errors
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        }
      });
    },
    
    // Helper function to convert old roles to tool permissions
    migrateToolPermissions(roles) {
      const toolPermissions = {};
      
      // Map roles to tool access
      if (roles.includes('Customer Service')) {
        toolPermissions.call_analysis = { access: true, manager: false };
        toolPermissions.agent_assist = { access: true, manager: false };
        toolPermissions.smart_search = { access: true, manager: false };
        toolPermissions.artificial_agent = { access: true, manager: false };
      }
      
      if (roles.includes('Operations')) {
        toolPermissions.switch_orders = { access: true, manager: false };
        toolPermissions.nameplate_automation = { access: true, manager: false };
        toolPermissions.damage_assessment = { access: true, manager: false };
        toolPermissions.forestry = { access: true, manager: false };
      }
      
      if (roles.includes('Communications')) {
        toolPermissions.content_manager = { access: true, manager: false };
        toolPermissions.imagen = { access: true, manager: false };
        toolPermissions.music = { access: true, manager: false };
        toolPermissions.podcasts = { access: true, manager: false };
      }
      
      if (roles.includes('Finance')) {
        toolPermissions.bill_prediction = { access: true, manager: false };
      }
      
      if (roles.includes('Human Resources')) {
        toolPermissions.workforce_automation = { access: true, manager: false };
      }
      
      if (roles.includes('Regulatory')) {
        toolPermissions.rate_application = { access: true, manager: false };
      }
      
      if (roles.includes('Technology')) {
        toolPermissions.chat_assistant = { access: true, manager: false };
      }
      
      // Pioneer access to experimental tools
      if (roles.includes('Pioneer')) {
        // Grant pioneer access to experimental features within their department
        Object.keys(toolPermissions).forEach(tool => {
          if (toolPermissions[tool]) {
            toolPermissions[tool].access = true;
          }
        });
      }
      
      return toolPermissions;
    },
    
    // Helper function to guess department from roles
    guessDepartmentFromRoles(roles) {
      if (roles.includes('Customer Service')) return 'Customer Service';
      if (roles.includes('Operations')) return 'Operations';
      if (roles.includes('Communications')) return 'Communications';
      if (roles.includes('Finance')) return 'Finance';
      if (roles.includes('Human Resources')) return 'Human Resources';
      if (roles.includes('Regulatory')) return 'Regulatory';
      if (roles.includes('Technology')) return 'Technology';
      if (roles.includes('Corporate')) return 'Corporate';
      
      return 'General';
    }
  }
}
</script>
<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background: url('@/assets/subtle-dots.png');
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
.view-container {
  background: red;
}
.flex-none {
  flex: none;
}
.flex-1 {
  flex: 1;
}
.flex-2 {
  flex: 2;
}
.flex-3 {
  flex: 3;
}
.flex-4 {
  flex: 4;
}
.width-100 {
  width: 100%;
}

.auth-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: black;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-loading h3 {
  color: white;
  font-weight: 300;
  margin-bottom: 8px;
}

.auth-loading p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}
</style>
